import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { FormTitle } from '../../components/FormTitle';
import { BusyIcon, LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import type { IslandStep } from '../../components/StepIsland/StepIsland';
import { DateRangeSelect, TRACKING_RANGES } from '../modules/DateRangeSelect';
import { ExportMenu } from '../../components/AppToolbar/ExportMenu';
import { FormMetaChips } from '../../components/AppToolbar';
import ResultEntryDetail, { type ResultEntryDetailHandle } from './ResultEntryDetail';
import {
  ResultEntryQueryPanel, EMPTY_REE_FILTERS,
  type ReeFilters, type ReeOptions, type ReeBatchOption,
} from './ResultEntryQueryPanel';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { guardedExit } from '../../hooks/useUnsavedGuard';
import { gridExportMatrix } from '../../components/AppDataGrid';
import { ResultEntryPendingSelector } from './ResultEntryPendingSelector';
import {
  buildPendingBatches, filterPendingJobs, pendingBatchCols, pendingJobCols,
  type PendingJob, type ReeFacetRow, type ReePendingBatch, type ReeSelTab,
} from './resultEntryPending';

// ─── Types (mirror ResultEntryListItem in the API) ───────────────────────────
export interface ResultEntryJob {
  jobID:        string;
  jobOid:       string;
  projectID:    string | null;
  projectName:  string | null;
  client:       string | null;
  receivedDate: string | null;
  statusText:   string;
  statusId:     string;
  pendingCount: number;
}

type DateRangeKey = '1M' | '3M' | '6M' | '1Y' | '2Y' | '5Y' | 'All';

/** The Query Panel's bottom "Pending Result Entry | QC Batch n | Job ID n" strip.
 *  Hidden for now (RTCFR "Simplify REE Pending Selection UI", 2026-09-27) since
 *  the Sample / QC Batch tab badges show the counts; flip to true to restore it. */
const SHOW_PENDING_SUMMARY = false;

/** Per-value pending-sample count for a Query-Panel combo (user request 2026-09-09).
 *  Each facet row carries its pending SAMPLE identity (sampleOid). The count for a
 *  value of `dim` = the number of DISTINCT samples having that value, among the rows
 *  matching every OTHER active filter (the caller passes rowsExcept(dim)). Counting
 *  distinct sampleOid — rather than summing — is what makes this correct for EVERY
 *  dimension: it collapses the QC-batch fan-out (same sample repeated per linked batch)
 *  and counts a multi-test sample once per dimension value (a sample appears on several
 *  rows, one per its pending tests). Cascades exactly like that combo's option list. */
/** Min/max of a yyyy-MM-dd date column across the given (filter-matched) rows — used
 *  to constrain the date pickers to the dates present for the current Test/filter
 *  combination (user request 2026-09-09). yyyy-MM-dd strings compare chronologically,
 *  so plain string min/max is correct; NULLs are ignored (no bound if none present). */
function dateBoundsFrom(rows: ReeFacetRow[], pick: (r: ReeFacetRow) => string | null): { min?: string; max?: string } {
  let min: string | undefined; let max: string | undefined;
  for (const r of rows) {
    const v = pick(r);
    if (!v) continue;
    if (min === undefined || v < min) min = v;
    if (max === undefined || v > max) max = v;
  }
  return { min, max };
}

function countsFrom(rows: ReeFacetRow[], dim: (r: ReeFacetRow) => string | null): Record<string, number> {
  const byVal = new Map<string, Set<string>>();
  for (const r of rows) {
    const v = dim(r);
    if (!v || !r.sampleOid) continue;
    let s = byVal.get(v);
    if (!s) { s = new Set<string>(); byVal.set(v, s); }
    s.add(r.sampleOid);
  }
  const counts: Record<string, number> = {};
  for (const [v, s] of byVal) counts[v] = s.size;
  return counts;
}

function cutoff(key: DateRangeKey): Date | null {
  if (key === 'All') return null;
  const n = new Date();
  const m: Record<Exclude<DateRangeKey, 'All'>, () => Date> = {
    '1M': () => new Date(n.getFullYear(), n.getMonth() - 1, n.getDate()),
    '3M': () => new Date(n.getFullYear(), n.getMonth() - 3, n.getDate()),
    '6M': () => new Date(n.getFullYear(), n.getMonth() - 6, n.getDate()),
    '1Y': () => new Date(n.getFullYear() - 1, n.getMonth(), n.getDate()),
    '2Y': () => new Date(n.getFullYear() - 2, n.getMonth(), n.getDate()),
    '5Y': () => new Date(n.getFullYear() - 5, n.getMonth(), n.getDate()),
  };
  return m[key as Exclude<DateRangeKey, 'All'>]();
}

const ymd = (d: Date) => {
  const pad = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Build a short human-readable label from the applied query panel filters.
 *  Priority: Batch ID > Job ID > Client > Project > Test > fallback count. */
function reeQueryLabel(f: ReeFilters, jobCount: number): string {
  const join = (arr: string[]) =>
    arr.slice(0, 3).join(', ') + (arr.length > 3 ? ` +${arr.length - 3} more` : '');
  if (f.batchId.length)     return `Batch: ${join(f.batchId)}`;
  if (f.id.length)          return `Job: ${join(f.id)}`;
  if (f.clientName.length)  return `Client: ${join(f.clientName)}`;
  if (f.projectId.length)   return `Project: ${join(f.projectId)}`;
  if (f.projectName.length) return `Project: ${join(f.projectName)}`;
  if (f.test.length)        return `Test: ${join(f.test)}`;
  return `${jobCount} job${jobCount === 1 ? '' : 's'}`;
}

/**
 * Result Entry Express (Analysis) — manual result entry. Single-route container:
 * the pending-entry LIST, and on Load the editable DETAIL (ResultEntryDetail),
 * with the Dynamic-Island stepper spanning both (Select Job → Load → Enter →
 * Save → Submit). Ported from legacy XAF Result Entry Express (read path live;
 * writes gated to a non-prod DB until approved).
 *
 * The structured query panel (ResultEntryQueryPanel) narrows the Job/Batch list
 * server-side before selection; the fuzzy-search row below it quick-filters the
 * returned rows. Both are distinct from the workflow Load action.
 */
export default function ResultEntryExpressPage() {
  const notify = useNotify();
  const [rows, setRows] = useState<ResultEntryJob[]>([]);
  const [loading, setLoading] = useState(false);
  // Grid stays EMPTY on open — records load only after the user runs a query.
  const [queried, setQueried] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  // Load-time for the initial data fetch that DOES happen on open — the filter
  // options (the list stays empty until a query is applied). Surfaced in FormTitle
  // so the "Loaded in X" chip appears on open like every other form (user report
  // 2026-09-05: time indicator missing on REE open).
  const [optionsLoadMs, setOptionsLoadMs] = useState<number | null>(null);
  const [_error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeKey>('3M');
  const [_selected, setSelected] = useState<Set<string | number>>(new Set());
  const [detailJob, setDetailJob] = useState<ResultEntryJob | null>(null);
  /** Multi-job mode: QC Batch matched 2+ jobs — all their samples load together. */
  const [detailJobs, setDetailJobs] = useState<ResultEntryJob[]>([]);
  const [_detailSteps, setDetailSteps] = useState<{ resultsDone: boolean; savedOnce: boolean; canSave: boolean; canSubmit: boolean }>({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
  const [detailLoadMs, setDetailLoadMs] = useState<number | null>(null);
  const [qpCollapsed, setQpCollapsed] = useState(false);
  const toggleQuery = useCallback(() => setQpCollapsed(c => !c), []);
  const detailRef = useRef<ResultEntryDetailHandle>(null);
  // Export payload published UP by the loaded grid — drives the "Export to" button
  // moved into the top control bar between Refresh and Query (user request 2026-09-05).
  const [reeExport, setReeExport] = useState<{ filename: string; headers: string[]; rows: string[][] }>(
    { filename: 'result-entry', headers: [], rows: [] });

  // Query panel — `draft` is edited in the panel; `applied` is what the query
  // actually uses (Apply copies draft → applied and reloads).
  const [draft, setDraft] = useState<ReeFilters>(EMPTY_REE_FILTERS);
  const [applied, setApplied] = useState<ReeFilters>(EMPTY_REE_FILTERS);
  // Always JOBID: the List SP's @BatchId filter narrows the job list to jobs in the
  // selected batch, which is exactly what the FilterOptions SP guarantees will return
  // results (it only offers batches whose jobs have pending regular parameters).
  // BATCHID mode required pending QC parameters on the batch itself — a different
  // condition that didn't match what the dropdown offered → "No jobs match".
  const searchBy: 'JOBID' | 'BATCHID' = 'JOBID';
  // Cascading query filters (task RTCFR, 2026-09-07) — instead of flat option
  // lists, we fetch the pending-entry universe's DISTINCT dimension-combinations
  // once per date window and derive every control's options CLIENT-SIDE so each
  // shows only values valid for the current filter combination (faceted search).
  const [facetRows, setFacetRows] = useState<ReeFacetRow[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);

  // Pending selection panel (RTCFR "REE Redesign — Pending Job / QC Batch
  // Selection", 2026-09-27): REE opens on a Sample | QC Batch list of what is
  // pending entry for the Date Range + Query Panel — the result grid appears
  // only after Load, in the SAME place (never both at once).
  const [pendingRows, setPendingRows] = useState<ResultEntryJob[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingLoadMs, setPendingLoadMs] = useState<number | null>(null);
  const [selTab, setSelTab] = useState<ReeSelTab>('Sample');
  const [selJobIds, setSelJobIds] = useState<Set<string | number>>(new Set());
  const [selBatchIds, setSelBatchIds] = useState<Set<string | number>>(new Set());
  const [visibleJobs, setVisibleJobs] = useState<PendingJob[]>([]);
  const [visibleBatches, setVisibleBatches] = useState<ReePendingBatch[]>([]);

  // The date window for the list + options: the quick Date-Range dropdown, unless
  // the panel's explicit Date Received overrides it (panel wins when set).
  const rangeWindow = useCallback(() => {
    const co = cutoff(dateRange);
    return { start: co ? ymd(co) : undefined, end: co ? ymd(new Date()) : undefined };
  }, [dateRange]);

  const load = useCallback(async () => {
    setLoading(true); setError(null); setSelected(new Set());
    const t0 = performance.now();
    try {
      const win = rangeWindow();
      const params: Record<string, string> = { searchBy };
      const start = applied.receivedFrom || win.start;
      const end   = applied.receivedTo   || win.end;
      if (start) params.startDate = start;
      if (end)   params.endDate   = end;
      // Checked-combo filters are multi-value → send them '|'-joined (the SP
      // matches ANY via STRING_SPLIT). Empty array = omit = no filter.
      const joinv = (a: string[]) => a.filter(Boolean).join('|');
      if (applied.clientName.length)     params.clientName     = joinv(applied.clientName);
      if (applied.projectId.length)      params.projectId      = joinv(applied.projectId);
      if (applied.projectName.length)    params.projectName    = joinv(applied.projectName);
      if (applied.test.length)           params.test           = joinv(applied.test);
      if (applied.matrix.length)         params.matrix         = joinv(applied.matrix);
      if (applied.id.length)             params.id             = joinv(applied.id);
      if (applied.batchId.length)        params.batchId        = joinv(applied.batchId);
      if (applied.sampleLocation.length) params.sampleLocation = joinv(applied.sampleLocation);
      if (applied.analyzedFrom)          params.analyzedFrom   = applied.analyzedFrom;
      if (applied.analyzedTo)            params.analyzedTo     = applied.analyzedTo;
      if (applied.collectedFrom)         params.collectedFrom  = applied.collectedFrom;
      if (applied.collectedTo)           params.collectedTo    = applied.collectedTo;

      const res = await api.get('/api/v1/result-entry', { params });
      setRows((res.data as { data: ResultEntryJob[] }).data ?? []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [rangeWindow, searchBy, applied]);

  // No auto-load on open — the results grid is empty until the user applies a
  // query. After the first Apply Query, changes to the criteria re-run the load.
  useEffect(() => { if (queried) void load(); }, [load, queried]);

  // Facet base rows for the query panel's CASCADING filters (task RTCFR,
  // 2026-09-07). Fetched once per date window (the quick Date Range + panel Date
  // Received override, mirroring the grid load's window) — the categorical
  // cascading itself is computed client-side from these rows, so narrowing a
  // control is instant (no round-trip).
  // NOTE (user request 2026-09-09): the facet is scoped by the QUICK Date Range window
  // ONLY — the panel's From/To date controls do NOT re-scope it. They are auto-filled to
  // the selected Test's date range (Test-to-date cascading) and filter the GRID on Load;
  // feeding them back into the facet would strand it at the previous Test's date range
  // when switching Tests (and could break the categorical cascade). The date BOUNDS the
  // controls snap to are derived client-side from this quick-range facet, so switching
  // Test always recomputes against the full window.
  const loadFacetRows = useCallback(async () => {
    setOptionsLoading(true);
    const t0 = performance.now();
    try {
      const win = rangeWindow();
      const params: Record<string, string> = {};
      if (win.start) params.startDate = win.start;
      if (win.end)   params.endDate   = win.end;
      const res = await api.get('/api/v1/result-entry/facet-rows', { params, silentLoading: true } as never);
      setFacetRows((res.data as { data: ReeFacetRow[] }).data ?? []);
    } catch {
      setFacetRows([]);   // pickers just show no options; not fatal
    } finally {
      setOptionsLoadMs(performance.now() - t0);
      setOptionsLoading(false);
    }
  }, [rangeWindow]);

  // The pending JOB list for the selection panel's Sample tab — the existing
  // result-entry list over the quick Date Range window only (same window as the
  // facet rows). Query Panel filters narrow it CLIENT-SIDE through the facet
  // (pendingJobs below), so editing a filter is instant — no round-trip.
  const loadPending = useCallback(async () => {
    setPendingLoading(true);
    const t0 = performance.now();
    try {
      const win = rangeWindow();
      const params: Record<string, string> = { searchBy: 'JOBID' };
      if (win.start) params.startDate = win.start;
      if (win.end)   params.endDate   = win.end;
      const res = await api.get('/api/v1/result-entry', { params, silentLoading: true } as never);
      setPendingRows((res.data as { data: ResultEntryJob[] }).data ?? []);
    } catch (e: unknown) {
      setPendingRows([]);
      notify.error(e instanceof Error ? `Failed to load pending jobs: ${e.message}` : 'Failed to load pending jobs.');
    } finally {
      setPendingLoadMs(performance.now() - t0);
      setPendingLoading(false);
    }
  }, [rangeWindow, notify]);

  useEffect(() => { void loadFacetRows(); void loadPending(); }, [loadFacetRows, loadPending]);

  // ── Cascading (faceted) option derivation ────────────────────────────────
  // A row satisfies a filter when that filter is empty OR the row's value is in
  // the selection. Each control's options come from rows matching every OTHER
  // filter (exclude-self), so a multi-select control still offers all of its own
  // values valid under the others, and every control narrows the rest.
  const rowsExcept = useCallback((except: keyof ReeFilters): ReeFacetRow[] => {
    const inSel = (sel: string[], v: string | null) => sel.length === 0 || (v != null && sel.includes(v));
    return facetRows.filter(r =>
      (except === 'batchId'        || inSel(draft.batchId, r.qcBatchID)) &&
      (except === 'id'             || inSel(draft.id, r.jobID)) &&
      (except === 'matrix'         || inSel(draft.matrix, r.matrix)) &&
      (except === 'test'           || inSel(draft.test, r.test)) &&
      (except === 'projectId'      || inSel(draft.projectId, r.projectID)) &&
      (except === 'projectName'    || inSel(draft.projectName, r.projectName)) &&
      (except === 'clientName'     || inSel(draft.clientName, r.client)) &&
      (except === 'sampleLocation' || inSel(draft.sampleLocation, r.sampleLocation)));
  }, [facetRows, draft]);

  // RTCFR (2026-09-17): AUTO-CASCADE date filter, ported from Result Tracking. Date-picker
  // BOUNDS = each date's available span over the CATEGORICAL-only facet (rowsExcept) — so the
  // auto-fill shows the range and the bounds can't feed back on themselves.
  const reeDateBounds = useMemo(() => ({
    analyzed:  dateBoundsFrom(rowsExcept('analyzedFrom'), r => r.analyzedDate),
    received:  dateBoundsFrom(rowsExcept('analyzedFrom'), r => r.receivedDate),
    collected: dateBoundsFrom(rowsExcept('analyzedFrom'), r => r.collectDate),
  }), [rowsExcept]);

  // A date end is an ACTIVE filter ONLY when the user TIGHTENS it inside the auto-filled span
  // (From > min or To < max); a value equal to its auto-bound (full span) or empty is INACTIVE.
  // So selecting a categorical value auto-fills the pickers but does NOT filter (count stays 165,
  // siblings remain); only genuine narrowing filters the counts, options AND the grid together.
  const activeDates = useMemo(() => {
    const from = (val: string, min?: string) => (val && (!min || val > min)) ? val : '';
    const to   = (val: string, max?: string) => (val && (!max || val < max)) ? val : '';
    return {
      analyzedFrom:  from(draft.analyzedFrom,  reeDateBounds.analyzed?.min),
      analyzedTo:    to  (draft.analyzedTo,    reeDateBounds.analyzed?.max),
      receivedFrom:  from(draft.receivedFrom,  reeDateBounds.received?.min),
      receivedTo:    to  (draft.receivedTo,    reeDateBounds.received?.max),
      collectedFrom: from(draft.collectedFrom, reeDateBounds.collected?.min),
      collectedTo:   to  (draft.collectedTo,   reeDateBounds.collected?.max),
    };
  }, [draft.analyzedFrom, draft.analyzedTo, draft.receivedFrom, draft.receivedTo,
      draft.collectedFrom, draft.collectedTo, reeDateBounds]);

  // rowsExcept + the ACTIVE date ranges — the SAME predicate the grid Load applies, with the
  // grid's per-field NULL rule (AI_Analysis_ResultEntry_List_SP): Analyzed KEEPS NULLs (line
  // "OR sp.AnalyzedDate IS NULL"), Received/Collected DROP NULLs when the bound is active. So a
  // NULL-CollectDate sample (e.g. 260917004-001) is counted while Collected is full-span/inactive
  // (combo 165 == grid 165) and drops from BOTH only if the user narrows Collected. Used by the
  // Sample Counts and the cascading option lists; date BOUNDS keep using the categorical-only facet.
  const rowsExceptDated = useCallback((except: keyof ReeFilters): ReeFacetRow[] => {
    const inYmd = (ymd: string | null, from: string, to: string, keepNull: boolean): boolean => {
      if (!from && !to) return true;
      if (!ymd) return keepNull;
      const d = ymd.slice(0, 10);
      if (from && d < from) return false;
      if (to   && d > to)   return false;
      return true;
    };
    return rowsExcept(except).filter(r =>
      inYmd(r.analyzedDate, activeDates.analyzedFrom, activeDates.analyzedTo, true)
      && inYmd(r.receivedDate, activeDates.receivedFrom, activeDates.receivedTo, false)
      && inYmd(r.collectDate,  activeDates.collectedFrom, activeDates.collectedTo, false));
  }, [rowsExcept, activeDates]);

  // Selection-panel rows: the facet rows matching EVERY Query Panel filter
  // (categorical + active dates — 'analyzedFrom' excludes no categorical filter),
  // i.e. the same predicate the combo counts and the grid Load use.
  const matchedFacet = useMemo(() => rowsExceptDated('analyzedFrom'), [rowsExceptDated]);
  const hasFacetFilter = !!(draft.batchId.length || draft.id.length || draft.test.length
    || draft.matrix.length || draft.clientName.length || draft.projectId.length
    || draft.projectName.length || draft.sampleLocation.length
    || Object.values(activeDates).some(Boolean));
  const pendingJobs = useMemo(
    () => filterPendingJobs(pendingRows, matchedFacet, hasFacetFilter),
    [pendingRows, matchedFacet, hasFacetFilter]);
  const pendingBatches = useMemo(() => buildPendingBatches(matchedFacet), [matchedFacet]);

  // Drop selections that a filter / Date Range change has taken off the list, so
  // Load never acts on a row the user can no longer see. Only removes → converges.
  useEffect(() => {
    const prune = (sel: Set<string | number>, valid: Set<string>) => {
      const kept = new Set(Array.from(sel).filter(id => valid.has(String(id))));
      return kept.size === sel.size ? sel : kept;
    };
    setSelJobIds(s => prune(s, new Set(pendingJobs.map(j => j.jobID))));
    setSelBatchIds(s => prune(s, new Set(pendingBatches.map(b => b.qcBatchID))));
  }, [pendingJobs, pendingBatches]);

  const options = useMemo<ReeOptions>(() => {
    const uniq = (vals: (string | null)[]) => Array.from(new Set(vals.filter((v): v is string => !!v)));
    const asc  = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    const desc = (a: string, b: string) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' });
    // QC Batch ID picker: Test + Method display columns (user request 2026-09-09).
    // RTCFR 2026-09-17: value lists + counts use rowsExceptDated (categorical + ACTIVE dates)
    // so they cascade with a user-narrowed date and equal the grid; date BOUNDS keep using the
    // categorical-only facet (reeDateBounds) so the auto-fill still shows each picker's full span.
    const seen = new Map<string, ReeBatchOption>();
    for (const r of rowsExceptDated('batchId'))
      if (r.qcBatchID && !seen.has(r.qcBatchID)) seen.set(r.qcBatchID, { id: r.qcBatchID, test: r.batchTest ?? '', method: r.batchMethod ?? '', emp: r.batchEmp ?? '' });
    return {
      clients:         uniq(rowsExceptDated('clientName').map(r => r.client)).sort(asc),
      projects:        uniq(rowsExceptDated('projectId').map(r => r.projectID)).sort(asc),
      projectNames:    uniq(rowsExceptDated('projectName').map(r => r.projectName)).sort(asc),
      tests:           uniq(rowsExceptDated('test').map(r => r.test)).sort(asc),
      matrices:        uniq(rowsExceptDated('matrix').map(r => r.matrix)).sort(asc),
      jobIds:          uniq(rowsExceptDated('id').map(r => r.jobID)).sort(desc),
      sampleLocations: uniq(rowsExceptDated('sampleLocation').map(r => r.sampleLocation)).sort(asc),
      batchIds:        Array.from(seen.values()).sort((a, b) => desc(a.id, b.id)),
      // Distinct pending-sample count per value (user request 2026-09-09), one cascaded
      // map per count-bearing combo (each built from rowsExceptDated(dim) via distinct
      // sampleOid = COUNT(DISTINCT SamplePK)). REE-only; the panel renders a "· Sample Count".
      counts: {
        test:           countsFrom(rowsExceptDated('test'),           r => r.test),
        matrix:         countsFrom(rowsExceptDated('matrix'),         r => r.matrix),
        projectId:      countsFrom(rowsExceptDated('projectId'),      r => r.projectID),
        projectName:    countsFrom(rowsExceptDated('projectName'),    r => r.projectName),
        clientName:     countsFrom(rowsExceptDated('clientName'),     r => r.client),
        sampleLocation: countsFrom(rowsExceptDated('sampleLocation'), r => r.sampleLocation),
      },
      // Date-picker bounds = each picker's full span over the categorical-only facet.
      dateBounds: reeDateBounds,
    };
  }, [rowsExceptDated, reeDateBounds]);

  // Pending-entry summary counts (the panel's bottom bar) — the FULL universe,
  // not the cascaded subset, so the "Pending Result Entry" totals stay stable.
  const fullJobs    = useMemo(() => new Set(facetRows.map(r => r.jobID).filter(Boolean)).size, [facetRows]);
  const fullBatches = useMemo(() => new Set(facetRows.map(r => r.qcBatchID).filter(Boolean)).size, [facetRows]);

  // Test-to-date auto-fill (user request 2026-09-09): when the CATEGORICAL selection
  // changes (Test, Client, Matrix, Project, Site, Job, QC Batch), populate the six date
  // controls with that combination's available date range so the dates actually SHOW
  // (min/max alone left the inputs blank). Gated on the categorical signature — NOT on
  // the dates themselves — so a user editing a date is never overwritten; only a
  // Test/filter change (or Clear) recalculates. A dimension with no dates (e.g. Analyzed
  // is NULL for pending-entry rows) clears to empty. The values are the FULL available
  // range, so on Load they don't narrow beyond the Test; the user may tighten them.
  const catSigRef = useRef<string>('');
  useEffect(() => {
    const catSig = JSON.stringify([draft.batchId, draft.id, draft.test, draft.matrix,
      draft.clientName, draft.projectId, draft.projectName, draft.sampleLocation]);
    if (catSig === catSigRef.current) return;
    catSigRef.current = catSig;
    // Only reflect dates once a categorical filter is active; with none selected leave
    // the controls empty (initial state) and clear them when the last filter is removed.
    const hasCat = !!(draft.batchId.length || draft.id.length || draft.test.length || draft.matrix.length
      || draft.clientName.length || draft.projectId.length || draft.projectName.length || draft.sampleLocation.length);
    const b = hasCat ? reeDateBounds : undefined;
    setDraft(prev => ({
      ...prev,
      analyzedFrom:  b?.analyzed?.min  ?? '', analyzedTo:  b?.analyzed?.max  ?? '',
      receivedFrom:  b?.received?.min  ?? '', receivedTo:  b?.received?.max  ?? '',
      collectedFrom: b?.collected?.min ?? '', collectedTo: b?.collected?.max ?? '',
    }));
  }, [draft.batchId, draft.id, draft.test, draft.matrix, draft.clientName,
      draft.projectId, draft.projectName, draft.sampleLocation, reeDateBounds]);

  // Prune any selected value that's no longer valid under the OTHER filters
  // (user request: "do not leave invalid selected values after another filter
  // changes"). Only removes values, so it converges; guarded so it never loops.
  useEffect(() => {
    if (facetRows.length === 0) return;
    const uniq = (vals: (string | null)[]) => new Set(vals.filter((v): v is string => !!v));
    const validFor: Record<string, Set<string>> = {
      batchId:        uniq(rowsExcept('batchId').map(r => r.qcBatchID)),
      id:             uniq(rowsExcept('id').map(r => r.jobID)),
      matrix:         uniq(rowsExcept('matrix').map(r => r.matrix)),
      test:           uniq(rowsExcept('test').map(r => r.test)),
      projectId:      uniq(rowsExcept('projectId').map(r => r.projectID)),
      projectName:    uniq(rowsExcept('projectName').map(r => r.projectName)),
      clientName:     uniq(rowsExcept('clientName').map(r => r.client)),
      sampleLocation: uniq(rowsExcept('sampleLocation').map(r => r.sampleLocation)),
    };
    const patch: Partial<ReeFilters> = {};
    for (const [k, valid] of Object.entries(validFor)) {
      const cur = draft[k as keyof ReeFilters] as string[];
      const kept = cur.filter(v => valid.has(v));
      if (kept.length !== cur.length) (patch as Record<string, string[]>)[k] = kept;
    }
    if (Object.keys(patch).length) setDraft(d => ({ ...d, ...patch }));
  }, [facetRows, draft, rowsExcept]);

  // Open / close the inline result grid — must be declared before loadAction so
  // the auto-open paths can call them directly.
  const openDetail = useCallback((job: ResultEntryJob) => {
    setDetailJob(job);
    setDetailJobs([]);
    setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
    setDetailLoadMs(null);
    setQpCollapsed(true);   // auto-collapse query panel when detail opens
    window.dispatchEvent(new CustomEvent('ai:nav-collapse'));
  }, []);

  /** Multi-job mode: all jobs in a QC batch load their samples together. */
  const openMultiDetail = useCallback((jobList: ResultEntryJob[]) => {
    setDetailJobs(jobList);
    setDetailJob(null);
    setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
    setDetailLoadMs(null);
    setQpCollapsed(true);
    window.dispatchEvent(new CustomEvent('ai:nav-collapse'));
  }, []);

  // Set true right before a query so that, once it returns, jobs are auto-opened —
  // single job → openDetail, multiple jobs → openMultiDetail (no picker shown).
  const pendingAutoLoad = useRef(false);

  // Back to the pending selection panel (breadcrumb): re-pull the pending lists
  // so jobs / batches submitted in the detail drop off.
  const closeDetail = useCallback(() => {
    setDetailJob(null);
    setDetailJobs([]);
    setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
    setDetailLoadMs(null);
    setQpCollapsed(false);  // restore query panel when returning to list
    pendingAutoLoad.current = false;
    setQueried(false);
    setRows([]);
    setSelJobIds(new Set()); setSelBatchIds(new Set());
    void loadFacetRows();
    void loadPending();
  }, [loadFacetRows, loadPending]);

  // Auto-clear the loaded job the instant ANY Query Panel control changes
  // (user request 2026-09-02) — previously Load stayed disabled after a job
  // loaded until the user manually clicked Clear first. `draft` only changes
  // via the panel's onChange (a real edit) or clearQuery — never as a side
  // effect of loadAction/openDetail/openMultiDetail — so this fires exactly
  // on user edits, never on the load/auto-open sequence itself. Clearing the
  // detail flips `inDetail` false, which is what re-enables the Load button
  // (its disabled condition is `loading || inDetail`) — no separate "enable"
  // flag needed. Applies uniformly to every control: combos (incl. the
  // uncheck-old/check-new multi-select case — each toggle is its own onChange)
  // and date fields alike.
  const prevDraftRef = useRef(draft);
  useEffect(() => {
    if (prevDraftRef.current === draft) return;
    prevDraftRef.current = draft;
    if (inDetail) {
      setDetailJob(null);
      setDetailJobs([]);
      setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
      setDetailLoadMs(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- inDetail is read, not a trigger: this must fire only on draft identity changes, not merely because inDetail flipped (e.g. right after openDetail sets detailJob, draft hasn't changed).
  }, [draft]);

  // Load the selected Job IDs (Sample tab) or QC Batch IDs (QC Batch tab): run the
  // existing query narrowed to them, then auto-open — 1 job → single-job detail,
  // 2+ → multi-job detail (a QC batch loads all of its jobs' samples, with
  // batchIds scoping the rows to that batch). The rest of the Query Panel still
  // applies as row filters inside the detail, exactly as before.
  const loadSelection = useCallback((tab: ReeSelTab, ids: string[]) => {
    if (ids.length === 0) {
      notify.warning(tab === 'Sample'
        ? 'Select one or more Job IDs to load.'
        : 'Select one or more QC Batch IDs to load.');
      return;
    }
    if (draft.receivedFrom && draft.receivedTo && draft.receivedFrom > draft.receivedTo) {
      notify.warning('Date Received “From” must be on or before “To”.');
      return;
    }
    if (draft.analyzedFrom && draft.analyzedTo && draft.analyzedFrom > draft.analyzedTo) {
      notify.warning('Date Analyzed “From” must be on or before “To”.');
      return;
    }
    if (draft.collectedFrom && draft.collectedTo && draft.collectedFrom > draft.collectedTo) {
      notify.warning('Date Collected “From” must be on or before “To”.');
      return;
    }
    pendingAutoLoad.current = true;
    setQueried(true);
    // RTCFR 2026-09-17: commit the ACTIVE dates (full-span auto-fill blanked to inactive) so the
    // grid Load applies the SAME date predicate the combo counts used — combo == grid always.
    setApplied({
      ...draft, ...activeDates,
      ...(tab === 'Sample' ? { id: ids } : { batchId: ids }),
    });   // triggers load() via the effect
  }, [draft, activeDates, notify]);

  const activeSel = selTab === 'Sample' ? selJobIds : selBatchIds;
  const loadAction = useCallback(
    () => loadSelection(selTab, Array.from(activeSel, String)),
    [loadSelection, selTab, activeSel]);

  // Double-click / Enter on a pending row → select just that row and Load it.
  const loadRow = useCallback((tab: ReeSelTab, id: string) => {
    if (tab === 'Sample') setSelJobIds(new Set([id])); else setSelBatchIds(new Set([id]));
    loadSelection(tab, [id]);
  }, [loadSelection]);

  // Auto-open when the query returns results — 1 job → single-job detail;
  // 2+ jobs → multi-job detail (all samples combined, no picker shown ever).
  useEffect(() => {
    if (pendingAutoLoad.current && !loading) {
      pendingAutoLoad.current = false;
      if (rows.length === 1 && rows[0]) {
        openDetail(rows[0]);
      } else if (rows.length > 1) {
        openMultiDetail(rows);
      }
    }
  }, [loading, rows, openDetail, openMultiDetail]);

  const clearQuery = useCallback(() => {
    setDraft(EMPTY_REE_FILTERS);
    setApplied(EMPTY_REE_FILTERS);
    // Return to the initial state: no job loaded, Query Panel expanded.
    pendingAutoLoad.current = false;
    setQueried(false);
    setRows([]); setSelected(new Set()); setLoadMs(null); setError(null);
    // Clear the result-entry grid — drop the loaded job so it returns to its
    // empty-shell state (same as page open, before Load is clicked).
    setDetailJob(null);
    setDetailJobs([]);
    setDetailSteps({ resultsDone: false, savedOnce: false, canSave: false, canSubmit: false });
    setDetailLoadMs(null);
    setQpCollapsed(false);   // expand query panel so the user can refine criteria
    // Back to the pending selection panel, reloaded for the current Date Range.
    setSelJobIds(new Set()); setSelBatchIds(new Set());
    void loadFacetRows();
    void loadPending();
  }, [loadFacetRows, loadPending]);

  // Refresh: always re-pull the option + pending lists; re-run the grid query
  // only if one has been applied.
  const refresh = useCallback(() => {
    void loadFacetRows();
    void loadPending();
    if (queried) void load();
  }, [loadFacetRows, loadPending, load, queried]);

  const inDetail = !!detailJob || detailJobs.length > 0;

  // Back (RTCFR "Add Back Button to REE Detail Grid", 2026-09-28): leave the
  // loaded result grid and return REE to its form-open stage — the pending
  // selection panel on the Sample tab, re-pulled for the CURRENT Date Range and
  // Query Panel filters (closeDetail). Nothing is saved or submitted: the shared
  // unsaved-changes confirm (standing rule 15) runs first, so entered-but-unsaved
  // results are never discarded silently. Also used by the breadcrumb.
  const backToSelection = useCallback(() => {
    void guardedExit(() => {
      closeDetail();
      setSelTab('Sample');
    });
  }, [closeDetail]);

  const canLoad =!loading && !inDetail && activeSel.size > 0;

  // "Export to" in selection mode exports the active pending tab's visible rows;
  // once a job is loaded the detail publishes its own grid (onExportData).
  useEffect(() => {
    if (inDetail) return;
    setReeExport(selTab === 'Sample'
      ? { filename: 'result-entry-pending-jobs', ...gridExportMatrix(visibleJobs, pendingJobCols()) }
      : { filename: 'result-entry-pending-qc-batches', ...gridExportMatrix(visibleBatches, pendingBatchCols()) });
  }, [inDetail, selTab, visibleJobs, visibleBatches]);

  // ── Module action keys (CLAUDE.md standing rule 14, §18.6, ADR-0013) — each
  //    def MIRRORS a top control-bar button's exact onClick handler + disabled
  //    gate, grouped under the form title.  Save / Submit drive the embedded
  //    detail (detailRef), matching the control-bar buttons.  Registered
  //    unconditionally, before the single return.
  useHotkeys([
    { combo: 'alt+r', label: 'Refresh', group: 'Result Entry Express', order: 1,
      run: () => refresh(), disabled: loading },
    // Back mirrors the control-bar Back button (shown only with a loaded grid).
    // Esc = Cancel / Close / Back per the reserved-key policy; never fires while
    // typing in a cell editor (allowInInput: false), and an open dialog owns Esc.
    { combo: 'esc', label: 'Back', group: 'Result Entry Express', order: 0,
      allowInInput: false, run: () => backToSelection(), disabled: !inDetail },
    { combo: 'alt+c', label: 'Clear', group: 'Result Entry Express', order: 2,
      run: () => clearQuery(), disabled: loading },
    { combo: 'alt+l', label: 'Load', group: 'Result Entry Express', order: 3,
      run: () => loadAction(), disabled: !canLoad },
    { combo: 'mod+s', label: 'Save', group: 'Result Entry Express', order: 4,
      run: () => detailRef.current?.save(), disabled: !_detailSteps.canSave },
    { combo: 'mod+enter', label: 'Submit', group: 'Result Entry Express', order: 5,
      run: () => detailRef.current?.submit(), disabled: !_detailSteps.canSubmit },
    { combo: 'alt+q', label: 'Query', group: 'Result Entry Express', order: 6,
      run: () => toggleQuery() },
  ]);

  // ── Stepper — Dynamic-Island bar spanning all 5 workflow steps. ─────────────
  const stepperSteps: IslandStep[] = [
    { id: 'select', label: 'Select Job/Batch', state: inDetail ? 'done' : 'active' },
    { id: 'load',   label: 'Load Samples',     state: inDetail ? 'done' : (activeSel.size > 0 || queried ? 'active' : 'todo') },
    { id: 'enter',  label: 'Enter Results',    state: !inDetail ? 'todo' : _detailSteps.resultsDone ? 'done' : 'active' },
    { id: 'save',   label: 'Save',             state: !inDetail ? 'todo' : _detailSteps.savedOnce ? 'done' : (_detailSteps.resultsDone ? 'active' : 'todo') },
    { id: 'submit', label: 'Submit',           state: !inDetail ? 'todo' : _detailSteps.savedOnce ? 'active' : 'todo' },
  ];

  // ── Single merged page ─────────────────────────────────────────────────────
  return (
    <div className="bl-page">
      <TopbarStepper steps={stepperSteps} />
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Analysis</span><i className="ti ti-chevron-right" />
            <span className={(detailJob || detailJobs.length > 0) ? 'dp-bc-link' : 'dp-bc-active'}
              style={(detailJob || detailJobs.length > 0) ? { cursor: 'pointer' } : undefined}
              onClick={(detailJob || detailJobs.length > 0) ? backToSelection : undefined}>
              Result Entry Express
            </span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="resexpress"
              title={detailJob
                ? `Result Entry Express — ${
                    (applied.batchId.length > 0 || applied.id.length > 0
                     || applied.clientName.length > 0 || applied.test.length > 0
                     || applied.projectName.length > 0)
                      ? reeQueryLabel(applied, 1)
                      : detailJob.jobID}`
                : detailJobs.length > 0
                  ? `Result Entry Express — ${reeQueryLabel(applied, detailJobs.length)}`
                  : undefined}
              auditFormName="Result Entry"
              auditSourceId={detailJob?.jobOid}
              // Chip source by state: a loaded detail → its load time; after a query
              // → the list load time; on open (no query yet) → the filter-options
              // load time, so the "Loaded in X" chip shows on open like every other
              // form (user report 2026-09-05).
              loadMs={(detailJob || detailJobs.length > 0) ? detailLoadMs : queried ? loadMs : (pendingLoadMs ?? optionsLoadMs)}
              loading={loading || optionsLoading || pendingLoading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Compact control bar: [Date Range | Refresh | Query toggle]   [Clear | Load] */}
        <div className="ree-ctrlbar">
          <div className="ree-ctrlbar-left">
            <DateRangeSelect variant="dropdown" label="Date Range" ranges={TRACKING_RANGES}
              value={TRACKING_RANGES.find(r => r.label === dateRange)?.days ?? null}
              onChange={(d) => setDateRange((TRACKING_RANGES.find(r => r.days === d)?.label ?? 'All') as DateRangeKey)} />
            <button type="button" className="ca-btn" onClick={refresh} disabled={loading}
              title={`Reload the current results. (${formatCombo('alt+r')})`}>
              <BusyIcon busy={loading} icon="ti-refresh" />&nbsp;Refresh
            </button>
            {/* Export to — moved here from the detail toolbar, between Refresh and
                Query (user request 2026-09-05). Exports the loaded grid's live rows
                (published up via onExportData). */}
            <ExportMenu filename={reeExport.filename} headers={reeExport.headers} rows={reeExport.rows} />
            <button type="button"
              className={`ca-btn ree-query-btn${qpCollapsed ? '' : ' ree-query-btn-active'}`}
              onClick={toggleQuery}
              title={(qpCollapsed ? 'Show query filters' : 'Hide query filters') + ` (${formatCombo('alt+q')})`}>
              <i className="ti ti-filter" aria-hidden="true" />&nbsp;Query&nbsp;
              <i className={`ti ti-chevron-${qpCollapsed ? 'down' : 'up'}`} aria-hidden="true" />
            </button>
            {/* Loaded-in-X + view-only Audit chips at the END of the left action
                group — after "Export to" (and the Query toggle), matching every
                shared <AppToolbar> form (BO UI Standard v3, user request
                2026-09-18: chips go after the last left-side action, globally).
                Registers the toolbar so <FormTitle> defers its top chips here. */}
            <FormMetaChips />
          </div>
          {/* Right group — canonical order Clear · Load · Save · Submit (user
              request 2026-09-05). Save / Submit were moved UP here from the detail's
              inline strip; they drive the loaded grid via detailRef and enable only
              when the embedded grid reports canSave / canSubmit (rows loaded + ticked
              + Write permission). When the grid is MAXIMIZED the detail renders its
              OWN Save / Submit near the maximize button (this bar is behind the
              full-screen overlay); minimizing returns them to here. */}
          <div className="ree-ctrlbar-right">
            {inDetail && (
              <button type="button" className="ca-btn" onClick={backToSelection}
                title={`Close the result grid and return to the pending Job / QC Batch selection. (${formatCombo('esc')})`}>
                <span aria-hidden="true">←</span>&nbsp;Back
              </button>
            )}
            <button type="button" className="ca-btn" onClick={clearQuery} disabled={loading}
              title={`Reset all query criteria and clear the grid. (${formatCombo('alt+c')})`}>
              <i className="ti ti-eraser" aria-hidden="true" />&nbsp;Clear
            </button>
            <button type="button" className="ca-btn ca-btn-solid" onClick={loadAction} disabled={!canLoad}
              title={(inDetail
                ? 'Clear, or change a Query Panel filter, to select a different job/batch.'
                : activeSel.size === 0
                  ? `Select one or more ${selTab === 'Sample' ? 'Job IDs' : 'QC Batch IDs'}, then Load.`
                  : `Load the ${activeSel.size} selected ${selTab === 'Sample'
                      ? (activeSel.size === 1 ? 'job' : 'jobs')
                      : (activeSel.size === 1 ? 'QC batch' : 'QC batches')} for result entry.`) + ` (${formatCombo('alt+l')})`}>
              {loading
                ? <><LoaderSymbol size={14} />&nbsp;Loading…</>
                : 'Load'}
            </button>
            <button type="button" className="ca-btn ca-btn-solid"
              onClick={() => detailRef.current?.save()} disabled={!_detailSteps.canSave}
              title={(_detailSteps.canSave
                ? 'Save the ticked results.'
                : 'Load a job and tick one or more results to save.') + ` (${formatCombo('mod+s')})`}>
              <i className="ti ti-device-floppy" aria-hidden="true" />&nbsp;Save
            </button>
            <button type="button" className="ca-btn ca-btn-solid"
              onClick={() => detailRef.current?.submit()} disabled={!_detailSteps.canSubmit}
              title={(_detailSteps.canSubmit
                ? 'Submit the ticked results for validation.'
                : _detailSteps.canSave && !_detailSteps.savedOnce
                  ? 'Save first, then Submit.'
                  : 'Load a job and tick one or more results to submit.') + ` (${formatCombo('mod+enter')})`}>
              <i className="ti ti-send" aria-hidden="true" />&nbsp;Submit
            </button>
          </div>
        </div>

        {/* Structured query panel — always visible (Query Panel stays on every
            state: browsing, selecting, or viewing results). */}
        <ResultEntryQueryPanel
          filters={draft}
          onChange={(patch) => setDraft(prev => ({ ...prev, ...patch }))}
          options={options}
          loadingOptions={optionsLoading}
          applying={loading}
          onApply={loadAction}
          onClear={clearQuery}
          collapsed={qpCollapsed}
          onCollapsedChange={setQpCollapsed}
          // Summary strip hidden for now (SHOW_PENDING_SUMMARY) — the tab badges
          // carry the counts. Omitting the props makes the panel skip that row.
          pendingBatches={SHOW_PENDING_SUMMARY ? fullBatches : undefined}
          pendingJobs={SHOW_PENDING_SUMMARY ? fullJobs : undefined}
          pendingLoading={optionsLoading}
          // Date Collected is now wired in REE (user request 2026-09-09) — enable its
          // From/To controls (analyzed + received were already enabled). Bound to
          // SampleLogIn.CollectDate, the same field the Analysis Queue popup shows.
          dateSoon={{ collected: false }}
        />

        {/* No-results notice */}
        {queried && !inDetail && rows.length === 0 && !loading && (
          <div className="ree-no-results">
            <i className="ti ti-inbox" aria-hidden="true" />
            No jobs match the current filters.
          </div>
        )}

        {/* Pending selection panel UNTIL a Job / QC Batch is loaded, then the
            result-entry grid in the same place — never both, and never the empty
            result grid on open (RTCFR 2026-09-27). */}
        {inDetail ? (
          <ResultEntryDetail
            ref={detailRef}
            job={detailJob}
            jobs={detailJobs.length > 0 ? detailJobs : undefined}
            batchIds={applied.batchId.length > 0 ? applied.batchId : undefined}
            rowFilters={applied}
            kind="job"
            onBack={closeDetail}
            inline
            onStepChange={setDetailSteps}
            onExportData={setReeExport}
            onLoadMs={setDetailLoadMs}
          />
        ) : (
          <ResultEntryPendingSelector
            tab={selTab}
            onTabChange={setSelTab}
            jobs={pendingJobs}
            batches={pendingBatches}
            selectedJobIds={selJobIds}
            onJobSelectionChange={setSelJobIds}
            selectedBatchIds={selBatchIds}
            onBatchSelectionChange={setSelBatchIds}
            loading={pendingLoading || optionsLoading}
            onLoadRow={loadRow}
            onVisibleJobsChange={setVisibleJobs}
            onVisibleBatchesChange={setVisibleBatches}
          />
        )}
      </div>
    </div>
  );
}
